import {useState, useEffect} from "react"
import {useSearchMessages} from "./useSearchMessages"
import {useQuery} from "@apollo/client"
import {SearchedThreadsQuery, SearchedThreadsDocument} from "@/gql/graphql"
import React from "react"
import {Profile, ThreadWithProfiles} from "./shared"

/**
 * Custom hook for searching threads with pagination support
 * Handles date filters, participant filters, and text search
 */
export function useSearchThreads(
  searchTerm?: string,
  dateRanges?: {start: string | any; end: string | any}[],
  participantProfileIds?: string[],
  page = 0,
  limit = 100
) {
  // Store all accumulated threads across pages
  const [threads, setThreads] = useState<ThreadWithProfiles[]>([])
  // Track current page threads separately
  const [currentPageThreads, setCurrentPageThreads] = useState<
    ThreadWithProfiles[]
  >([])
  const [searchResults, setSearchResults] = useState<
    {thread_uid: string; message_id: string}[]
  >([])
  const [isFetchingThreads, setIsFetchingThreads] = useState(false)

  /**
   * Reset thread state when search parameters change
   * Note: We don't set page=0 here to avoid circular dependency issues
   */
  useEffect(() => {
    // Reset all thread state when search parameters change
    setThreads([])
    setCurrentPageThreads([])
    setSearchResults([])

    // Note: We don't call setPage(0) here as it would create a circular dependency
    // The parent component should handle resetting the page when search params change
  }, [searchTerm, dateRanges, participantProfileIds])
  const {
    data,
    error,
    isLoading: isSearchLoading,
    hasMore,
  } = useSearchMessages(
    searchTerm,
    dateRanges,
    participantProfileIds,
    page,
    limit
  )

  // We can do a GQL query to get the threads in parallel or after the messages load
  const {client} = useQuery(SearchedThreadsDocument)

  /**
   * Fetch threads whenever search results change
   * Handles data accumulation across pages
   */
  useEffect(() => {
    // Skip thread fetch if no message results
    if (!data || data.length === 0) {
      // Only clear if it's the first page to avoid losing previous results
      if (page === 0) {
        setThreads([])
        setCurrentPageThreads([])
        setSearchResults([])
      }
      return
    }

    const fetchThreads = async () => {
      setIsFetchingThreads(true)
      try {
        // Extract the unique thread_uids from message results
        const uniqueThreadIds = [...new Set(data.map((m) => m.thread_uid))]

        // Query GraphQL to get full thread data
        const {data: threadsData} = await client.query({
          query: SearchedThreadsDocument,
          variables: {
            filter: {
              thread_uid: {in: uniqueThreadIds},
            },
          },
        })

        // Process and sanitize thread data
        const sanitizedThreads = sanitizeResponse(threadsData)

        // Create a map for quick thread lookup by ID
        const threadMap = new Map(
          sanitizedThreads.map((thread) => [thread.thread_uid, thread])
        )

        // Get threads in the same order as the search results
        let orderedThreads = uniqueThreadIds
          .map((id) => threadMap.get(id))
          .filter(Boolean) as ThreadWithProfiles[]

        // Ensure threads are properly sorted by timestamp (most recent first)
        orderedThreads = orderedThreads.sort((a, b) => {
          // Use last_message_timestamp for sorting if available
          const timeA = a.last_message_timestamp
            ? new Date(a.last_message_timestamp).getTime()
            : 0
          const timeB = b.last_message_timestamp
            ? new Date(b.last_message_timestamp).getTime()
            : 0
          return timeB - timeA // Descending order (newest first)
        })

        // Update current page threads
        setCurrentPageThreads(orderedThreads)

        // Accumulate threads across pages
        setThreads((prevThreads) => {
          if (page === 0) {
            // First page - replace existing threads
            return orderedThreads
          } else {
            // Add new threads to existing collection
            // Then re-sort the entire collection to ensure proper ordering
            const combinedThreads = [...prevThreads, ...orderedThreads]

            // Remove any duplicates that might appear across pages
            const uniqueThreads = Array.from(
              new Map(
                combinedThreads.map((thread) => [thread.thread_uid, thread])
              ).values()
            )

            // Sort by timestamp (newest first)
            return uniqueThreads.sort((a, b) => {
              const timeA = a.last_message_timestamp
                ? new Date(a.last_message_timestamp).getTime()
                : 0
              const timeB = b.last_message_timestamp
                ? new Date(b.last_message_timestamp).getTime()
                : 0
              return timeB - timeA
            })
          }
        })

        // Accumulate search results
        setSearchResults((prevResults) => {
          if (page === 0) {
            return data
          } else {
            return [...prevResults, ...data]
          }
        })
      } catch (error) {
        // Handle errors gracefully - only clear state on first page
        if (page === 0) {
          setThreads([])
          setCurrentPageThreads([])
          setSearchResults([])
        }
      } finally {
        setIsFetchingThreads(false)
      }
    }

    fetchThreads()
  }, [data, client, page])

  // Update the memoized result to include combined loading state
  const result = React.useMemo(
    () => ({
      threads,
      currentPageThreads,
      searchResults,
      error,
      isLoading: isSearchLoading || isFetchingThreads,
      hasMore,
      page,
      totalCount: typeof data?.length === "number" ? data.length : 0,
    }),
    [
      threads,
      currentPageThreads,
      searchResults,
      error,
      isSearchLoading,
      isFetchingThreads,
      hasMore,
      page,
      data?.length,
    ]
  )

  return result
}

/**
 * Sanitizes the GraphQL response and transforms it into a usable format
 * Extracts required fields and handles nested data structure
 */
function sanitizeResponse(
  threadsData: SearchedThreadsQuery
): ThreadWithProfiles[] {
  const threads = threadsData?.threadsCollection?.edges.map(
    (edge: any) => edge.node
  )
  if (!threads) {
    throw new Error("No thread data found")
  }

  return threads.map((thread: any) => {
    // Extract profiles from participants collection
    const profiles =
      thread?.message_groups?.group_participantsCollection?.edges.map(
        (edge: any) => edge.node.profiles
      )
    if (!profiles) {
      throw new Error(`No profiles data found on thread ${thread.thread_uid}`)
    }

    // Extract first message subject for title
    const firstMessageSubject =
      thread.thread_messagesCollection?.edges[0]?.node?.messages?.subject

    // Return normalized thread data
    return {
      thread_uid: thread.thread_uid,
      created_at: thread.created_at,
      title: firstMessageSubject,
      description: thread.description,
      last_message_timestamp: thread.last_message_timestamp,
      profiles: profiles as Profile[],
    }
  })
}
